import type { RouteRecordRaw } from 'vue-router';
import LoadingView from '@/views/misc/LoadingView.vue';
import TimeoutView from '@/views/misc/TimeoutView.vue';
import { type Component, defineAsyncComponent, defineComponent, h } from 'vue';
import Logger from 'js-logger';
import type { UserRole } from '@/types/auth';
import type { I18nModuleName } from '@/i18n';

// Participant module components
const JoinOrganizationView = (): Promise<Component> => lazyLoadView(import('@/views/auth/JoinOrganizationView.vue'));
const OrganizationJoinView = (): Promise<Component> => lazyLoadView(import('@/views/auth/OrganizationJoinView.vue'));
const StartView = (): Promise<Component> => lazyLoadView(import('@/views/simulation/StartView.vue'));
const ParticipantsView = (): Promise<Component> => lazyLoadView(import('@/views/ParticipantsView.vue'));
const SimulationsView = (): Promise<Component> => lazyLoadView(import('@/views/simulation/SimulationsView.vue'));
const PromptsView = (): Promise<Component> => lazyLoadView(import('@/views/admin/prompts/PromptView.vue'));
const SimulationView = (): Promise<Component> => lazyLoadView(import('@/views/simulation/SimulationView.vue'));
const CoursesView = (): Promise<Component> => lazyLoadView(import('@/views/user/CoursesView.vue'));
const CourseDetailsView = (): Promise<Component> => lazyLoadView(import('@/views/user/CourseDetailsView.vue'));
const CourseTaskViewWrapper = (): Promise<Component> => lazyLoadView(import('@/views/user/CourseTaskViewWrapper.vue'));
const RolePermissionView = (): Promise<Component> => lazyLoadView(import('@/views/admin/roles/RolePermissionView.vue'));
const NotAuthorizedView = (): Promise<Component> => lazyLoadView(import('@/views/misc/NotAuthorizedView.vue'));
const SkillProgressView = (): Promise<Component> => lazyLoadView(import('@/views/user/SkillProgressView.vue'));

// Instructor module components
const LoginView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/LoginView.vue'));
const PhoneLoginView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/PhoneLoginView.vue'));
// const SetPasswordView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/SetPasswordView.vue'));
// const PGALoginView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/PGALoginView.vue'));
const SignupView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/SignupView.vue'));
const ForgotPasswordView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/ForgotPasswordView.vue'));
const ResetPasswordView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/ResetPasswordView.vue'));
const AdminView = (): Promise<Component> => lazyLoadView(import('@/views/admin/AdminView.vue'));
const SimulationsManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/simulations/SimulationsManagementView.vue'));
const CreateSimulationWizardView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/simulations/CreateSimulationWizardView.vue'));
const EditSimulationWizardView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/simulations/EditSimulationWizardView.vue'));
const AdminAccountSettingsView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/settings/AdminAccountSettingsView.vue'));
const AdminAccountSettingsGeneralView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/settings/AdminAccountSettingsGeneralView.vue'));
const AdminAccountSettingsSecurityView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/settings/AdminAccountSettingsSecurityView.vue'));
const ConsentManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/settings/ConsentManagementView.vue'));
const UsersManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/settings/UsersManagementView.vue'));
const SetupAccountView = (): Promise<Component> => lazyLoadView(import('@/views/admin/auth/SetupAccountView.vue'));
const OrganizationsManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/organizations/OrganizationsManagementView.vue'));
const OrgLicensePoolView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/licensing/OrgLicensePoolView.vue'));
const LicensingCohortsView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/licensing/LicensingCohortsView.vue'));
const LicenseReportView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/licensing/LicenseReportView.vue'));
const OrganizationSettingsView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/organization/OrganizationSettingsView.vue'));
const OrganizationBrandingView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/organization/OrganizationBrandingView.vue'));
const OrganizationManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/organization/OrganizationManagementView.vue'));
const DocumentManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/documents/DocumentManagementView.vue'));
const OnboardingWizardView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/onboarding/OnboardingWizardView.vue'));
const NotificationsManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/notification/NotificationsManagementView.vue'));
const TagsManagementView = (): Promise<Component> => lazyLoadView(import('@/views/tags/TagsManagementView.vue'));
const LTILaunchView = (): Promise<Component> => lazyLoadView(import('@/views/lti/LTILaunchView.vue'));
const AdminDashboardView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/dashboard/AdminDashboardView.vue'));
const TeamSkillProgressView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/reporting/TeamSkillProgressView.vue'));
const TeamActivityReportView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/reporting/TeamActivityReportView.vue'));
const ModulePerformanceView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/reporting/ModulePerformanceView.vue'));
const AiAssistantUsageReportView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/reporting/AiAssistantUsageReportView.vue'));
// const TeamRoleReadinessView = (): Promise<Component> =>
//     lazyLoadView(import('@/views/admin/reporting/TeamRoleReadinessView.vue'));

// Editor module components
const ContentEditorView = (): Promise<Component> => lazyLoadView(import('@/editor/core/views/ContentEditorView.vue'));
const CoursesAdminView = (): Promise<Component> => lazyLoadView(import('@/editor/core/views/CoursesAdminView.vue'));
const CourseDetailsEditorView = (): Promise<Component> =>
    lazyLoadView(import('@/editor/core/views/CourseDetailsEditorView.vue'));
const CourseCreatorView = (): Promise<Component> => lazyLoadView(import('@/editor/core/views/CourseCreatorView.vue'));
const CompetenciesManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/competencies/CompetenciesManagementView.vue'));
const DepartmentsManagementView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/departments/DepartmentsManagementView.vue'));
const TemplateBrowserView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/templates/TemplateBrowser.vue'));
const IndustryTemplateCreatorView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/templates/IndustryTemplateCreator.vue'));
const IndustryBrowserView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/industries/IndustryBrowser.vue'));
const UserAnalyticsDashboardView = (): Promise<Component> =>
    lazyLoadView(import('@/views/admin/UserAnalyticsDashboardView.vue'));
const DashboardViewV2 = (): Promise<Component> => lazyLoadView(import('@/views/user/DashboardViewV2.vue'));

const MteSimAccessView = (): Promise<Component> => lazyLoadView(import('@/views/MteSimAccessView.vue'));

const NotFoundView = (): Promise<Component> => lazyLoadView(import('@/views/misc/NotFoundView.vue'));
const FaqView = (): Promise<Component> => lazyLoadView(import('@/views/misc/FaqView.vue'));

declare module 'vue-router' {
    interface RouteMeta {
        // If true, the user does not need to be authenticated to access the route.
        noAuth?: boolean;
        // Name of the route to redirect to if the user is not authenticated. Defaults to 'login'.
        redirectOnMissingAuth?: string;
        // Roles that are allowed to access the route.
        requiresRole?: UserRole[];
        // If true, will show a team selection dropdown in the sidebar for instructors.
        allowTeamSelection?: boolean;
        // If true, the currentTeamKey in useTeamSelection can return undefined for instructors.
        hasInstructorMode?: boolean;
        // If true, the route requires a organization to be selected.
        requireOrganizationSelection?: boolean;
        // The permission that is required to access the route.
        requiredPermission?: string | string[];
        // If true, requires super admin access (admin role in default organization).
        requiresSuperAdmin?: boolean;
        // If true, blocks access when the org has a pool but the user's license is not active.
        requiresActiveLicense?: boolean;
        // If true, requires the organization's metadata.enableAIAssistant flag to be true.
        requiresAIAssistant?: boolean;
        // i18n module to lazy-load when this route (or any ancestor) is navigated to.
        // All modules declared across the matched route hierarchy are loaded in parallel.
        i18nModule?: I18nModuleName;
        // If true, blocks this route (and its children) when the org has disableAccountSettings set.
        requiresAccountSettingsEnabled?: boolean;
        // i18n key resolving to this route's page title (shown as "{title} · {base site title}" in the
        // browser tab). Reused as the fallback for routes whose title is otherwise set dynamically via
        // usePageTitle (see src/composables/usePageTitle.ts), e.g. course/simulation names.
        titleKey?: string;
    }
}

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'home',
        component: () => import('../views/misc/HomeRedirectView.vue'),
    },
    // ============== MTE Route ==============
    {
        path: '/mte/:scenarioId',
        name: 'mte',
        meta: { noAuth: true, i18nModule: 'app' },
        component: MteSimAccessView,
    },
    // ============== Organization invite routes ==============
    {
        path: '/organizations/accept-invite/:organizationInviteId',
        name: 'joinOrganization',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'organizations.joinOrganization' },
        component: JoinOrganizationView,
    },
    {
        path: '/org/:orgCode/join',
        name: 'organizationJoin',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'organizations.joinOrganization' },
        component: OrganizationJoinView,
    },
    // ============== Auth routes ==============
    {
        path: '/login',
        name: 'login',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'auth.login' },
        component: LoginView,
        // component: PGALoginView,
    },
    {
        path: '/login/phone',
        name: 'phoneLogin',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'auth.login' },
        component: PhoneLoginView,
    },
    // {
    //     noAuth: true - reached cold from an email link; SetPasswordView verifies the token itself.
    //     path: '/set-password',
    //     name: 'setPassword',
    //     meta: { noAuth: true, i18nModule: 'auth' },
    //     component: SetPasswordView,
    // },
    // {
    //     path: '/normal-login',
    //     name: 'normalLogin',
    //     meta: { noAuth: true, i18nModule: 'auth' },
    //     component: LoginView,
    // },
    {
        path: '/sign-up',
        name: 'signUp',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'auth.letsGetStarted' },
        component: SignupView,
    },
    {
        path: '/forgot-password',
        name: 'forgotPassword',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'auth.forgotPasswordCap' },
        component: ForgotPasswordView,
    },
    {
        path: '/setup-account',
        name: 'setupAccount',
        meta: { i18nModule: 'auth', titleKey: 'auth.setupAccountCap' },
        component: SetupAccountView,
    },
    {
        path: '/reset-password',
        name: 'resetPassword',
        meta: { i18nModule: 'auth', titleKey: 'auth.resetPasswordCap' },
        component: ResetPasswordView,
    },
    {
        path: '/confirm-email-change',
        redirect: { name: 'accountSettingsGeneral' },
    },
    // ============== LTI Routes ==============
    {
        path: '/lti/launch',
        name: 'ltiLaunch',
        meta: { noAuth: true },
        component: LTILaunchView,
    },
    // ============== Participant module routes ==============
    {
        path: '/start',
        name: 'start',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'auth.welcome' },
        component: StartView,
    },
    {
        path: '/app',
        component: ParticipantsView,
        meta: { requireOrganizationSelection: true, i18nModule: 'app' },
        children: [
            {
                path: '',
                // Org-specific overrides (metadata.navigation.defaultParticipantRoute) are applied
                // in the router's beforeEach guard, which can await organization data before
                // deciding the final destination — this static redirect can't await anything.
                redirect: { name: 'dashboard' },
            },
            {
                path: 'courses',
                name: 'courses',
                component: CoursesView,
                meta: { titleKey: 'app.navigation.myModules' },
            },
            {
                path: 'course-details/:id',
                name: 'courseDetails',
                component: CourseDetailsView,
                meta: { titleKey: 'app.courses.courseDetails.pageTitle' },
            },
            {
                path: 'simulations',
                name: 'simulations',
                component: SimulationsView,
                meta: { titleKey: 'app.navigation.simulations' },
            },
            {
                path: 'dashboard',
                name: 'dashboard',
                component: DashboardViewV2,
                meta: { titleKey: 'app.navigation.dashboard' },
            },
            {
                path: 'skill-progress',
                name: 'skillProgress',
                component: SkillProgressView,
                meta: { titleKey: 'app.navigation.userReports' },
            },
            {
                path: 'faq',
                name: 'faq',
                component: FaqView,
                meta: { titleKey: 'app.faq.pageTitle' },
            },
            {
                path: 'settings/account',
                component: AdminAccountSettingsView,
                meta: { requiresAccountSettingsEnabled: true },
                children: [
                    {
                        path: '',
                        name: 'accountSettings',
                        redirect: '/app/settings/account/general',
                    },
                    {
                        path: 'general',
                        name: 'accountSettingsGeneral',
                        component: AdminAccountSettingsGeneralView,
                        meta: { i18nModule: 'auth', titleKey: 'admin.settings.general' },
                    },
                    {
                        path: 'security',
                        name: 'accountSettingsSecurity',
                        component: AdminAccountSettingsSecurityView,
                        meta: { i18nModule: 'auth', titleKey: 'admin.settings.security' },
                    },
                ],
            },
            {
                path: 'notifications',
                name: 'notificationsManagementParticipants',
                component: NotificationsManagementView,
                meta: { titleKey: 'app.navigation.notifications' },
            },
        ],
    },
    // route for activity for participant
    {
        path: '/task/:id',
        name: 'courseTask',
        component: CourseTaskViewWrapper,
        meta: {
            redirectOnMissingAuth: 'login',
            i18nModule: 'app',
            titleKey: 'app.courses.activity.singular.atSentenceStart',
        },
    },
    {
        path: '/simulations/:simulationID',
        name: 'simulation',
        // No static titleKey: the scenario's own title (via usePageTitle in SimulationView.vue) takes
        // over almost immediately, and no existing string fits as a meaningful static fallback.
        meta: { redirectOnMissingAuth: 'start', i18nModule: 'app' },
        component: SimulationView,
    },
    {
        // The simulation routes are defined in the simulation definition and are added dynamically. This route catches the case where the user tries to access
        // a simulation route before the simulation has been initialized. It redirects to the simulation view, which will initialize the simulation and then redirect to the
        // original route.
        path: '/simulations/:simulationID/:pathMatch(.*)*',
        redirect: (to) => {
            // We actually don't need to pass the simulationID param to the new route, since it already exists in this matched route.
            // But if we don't define the params manually, the 'pathMatch' param is also passed to the new route, which results in a warning.
            return {
                name: 'simulation',
                query: { postInitRedirect: to.fullPath },
                params: { simulationID: to.params.simulationID },
            };
        },
    },
    // ============== Instructor module routes ==============
    {
        path: '/admin',
        meta: {
            requiresRole: ['instructor', 'admin', 'department_admin', 'manager'],
            requireOrganizationSelection: true,
            i18nModule: 'app',
        },
        component: AdminView,
        children: [
            {
                path: '',
                // Org-specific overrides (metadata.navigation.defaultAdminRoute) are applied in
                // the router's beforeEach guard, which can await organization data before
                // deciding the final destination — this static redirect can't await anything.
                redirect: { name: 'adminDashboard' },
            },
            {
                path: 'simulations',
                name: 'simulationsManagement',
                component: SimulationsManagementView,
                meta: { titleKey: 'app.navigation.simulations' },
            },
            {
                path: 'simulations/create',
                name: 'simulationCreationWizard',
                component: CreateSimulationWizardView,
                meta: { titleKey: 'admin.simulationsManagement.createSimulation' },
            },
            {
                path: 'simulations/:simulationID/edit',
                name: 'simulationEditWizard',
                component: EditSimulationWizardView,
                meta: { titleKey: 'admin.simulationsManagement.editSimulation' },
            },
            {
                path: 'prompts',
                name: 'promptsManagement',
                component: PromptsView,
                meta: { titleKey: 'app.navigation.prompts' },
            },
            {
                path: 'users',
                name: 'usersManagement',
                component: UsersManagementView,
                meta: { titleKey: 'app.navigation.users' },
            },
            {
                path: 'organization',
                name: 'organizationManagement',
                component: OrganizationManagementView,
                meta: { titleKey: 'app.navigation.organization' },
                children: [
                    {
                        path: 'settings',
                        name: 'organizationSettings',
                        component: OrganizationSettingsView,
                        meta: { titleKey: 'app.navigation.organizationGeneralSettings' },
                    },
                    {
                        path: 'branding',
                        name: 'organizationBranding',
                        component: OrganizationBrandingView,
                        meta: { titleKey: 'app.navigation.organizationBranding' },
                    },
                ],
            },
            {
                path: 'role-permission-view',
                name: 'rolePermissionView',
                component: RolePermissionView,
                meta: { requiresSuperAdmin: true, titleKey: 'app.navigation.rolePermissionView' },
            },
            {
                path: 'competencies',
                name: 'competenciesManagement',
                component: CompetenciesManagementView,
                meta: { titleKey: 'app.navigation.competencies' },
            },
            {
                path: 'departments',
                name: 'departmentsManagement',
                component: DepartmentsManagementView,
                meta: { requiredPermission: 'manage_departments', titleKey: 'app.navigation.departments' },
            },

            // Editor routes
            {
                path: 'courses',
                name: 'coursesManagement',
                component: CoursesAdminView,
                meta: { titleKey: 'app.navigation.courses' },
            },
            {
                path: 'course-details/:courseId',
                name: 'courseDetailsEditor',
                component: CourseDetailsEditorView,
                meta: {
                    requiredPermission: 'manage_courses',
                    requiresActiveLicense: true,
                    titleKey: 'cms.editCourseDetails',
                },
            },
            {
                path: 'content-editor',
                name: 'contentEditor',
                component: ContentEditorView,
                meta: {
                    requiredPermission: 'manage_courses',
                    requiresActiveLicense: true,
                    titleKey: 'cms.pageTitle',
                },
            },
            {
                path: 'course-creator',
                name: 'courseCreator',
                component: CourseCreatorView,
                meta: {
                    requiredPermission: 'manage_courses',
                    requiresActiveLicense: true,
                    titleKey: 'app.courses.courseModuleCreatorAI.title',
                },
            },
            {
                path: 'organizations',
                name: 'organizationsManagement',
                component: OrganizationsManagementView,
                meta: { requiresRole: ['admin'], titleKey: 'app.navigation.organizations' },
            },
            {
                path: 'organizations/:organizationId/license-pool',
                name: 'orgLicensePool',
                component: OrgLicensePoolView,
                meta: { requiresSuperAdmin: true, titleKey: 'admin.licensing.poolTitle' },
            },
            {
                path: 'licensing',
                name: 'licensingAndCohorts',
                component: LicensingCohortsView,
                meta: { requiredPermission: 'manage_licenses', titleKey: 'app.navigation.licensingAndCohorts' },
            },
            {
                path: 'licenses/report',
                name: 'licenseReport',
                component: LicenseReportView,
                meta: { requiredPermission: 'manage_licenses', titleKey: 'app.navigation.licenseReport' },
            },
            {
                path: 'settings/account',
                component: AdminAccountSettingsView,
                meta: { requiresAccountSettingsEnabled: true },
                children: [
                    {
                        path: '',
                        redirect: '/admin/settings/account/general',
                    },
                    {
                        path: 'general',
                        name: 'adminAccountSettingsGeneral',
                        component: AdminAccountSettingsGeneralView,
                        meta: { i18nModule: 'auth', titleKey: 'admin.settings.general' },
                    },
                    {
                        path: 'security',
                        name: 'adminAccountSettingsSecurity',
                        component: AdminAccountSettingsSecurityView,
                        meta: { i18nModule: 'auth', titleKey: 'admin.settings.security' },
                    },
                ],
            },
            {
                path: 'documents',
                name: 'documentManagement',
                component: DocumentManagementView,
                meta: { titleKey: 'app.navigation.documents' },
            },
            {
                path: 'templates',
                name: 'templates',
                component: TemplateBrowserView,
                meta: { requiresRole: ['admin'], titleKey: 'app.navigation.industryTemplates' },
            },
            {
                path: 'templates/create',
                name: 'create-template',
                component: IndustryTemplateCreatorView,
                meta: { requiresRole: ['admin'], titleKey: 'admin.templates.createIndustryTemplate' },
            },
            {
                path: 'templates/edit/:id',
                name: 'edit-template',
                component: IndustryTemplateCreatorView,
                meta: { requiresRole: ['admin'], titleKey: 'admin.templates.editIndustryTemplate' },
            },
            {
                path: 'industries',
                name: 'industries',
                component: IndustryBrowserView,
                meta: { requiresRole: ['admin'], titleKey: 'app.navigation.industries' },
            },
            {
                path: 'onboarding',
                name: 'onboarding',
                component: OnboardingWizardView,
                meta: { requiresRole: ['admin'], titleKey: 'admin.onboarding.wizard.title' },
            },
            {
                path: 'notifications',
                name: 'notificationsManagement',
                component: NotificationsManagementView,
                meta: { titleKey: 'app.navigation.notifications' },
            },
            {
                path: 'consent-management',
                name: 'consentManagement',
                component: ConsentManagementView,
                meta: { requiresSuperAdmin: true, titleKey: 'app.navigation.consentManagement' },
            },
            {
                path: 'tags',
                name: 'tags',
                component: TagsManagementView,
                meta: { titleKey: 'app.navigation.tags' },
            },
            {
                path: 'user-analytics',
                name: 'userAnalytics',
                component: UserAnalyticsDashboardView,
                meta: { requiresSuperAdmin: true, titleKey: 'app.navigation.userAnalytics' },
            },
            {
                path: 'admin-dashboard',
                name: 'adminDashboard',
                component: AdminDashboardView,
                meta: { titleKey: 'app.navigation.adminDashboard' },
            },
            {
                path: 'readiness-and-skills',
                name: 'readinessAndSkills',
                component: TeamSkillProgressView,
                meta: { titleKey: 'app.navigation.readinessAndSkills' },
            },
            {
                path: 'activity-report',
                name: 'activityReport',
                component: TeamActivityReportView,
                meta: { titleKey: 'app.navigation.activityReport' },
            },
            {
                path: 'activity-report/module-performance',
                name: 'modulePerformance',
                component: ModulePerformanceView,
                meta: { titleKey: 'admin.reportingAndAnalytics.modulePerformanceTitle' },
            },
            {
                path: 'ai-assistant-usage-report',
                name: 'aiAssistantUsageReport',
                component: AiAssistantUsageReportView,
                meta: {
                    requiredPermission: 'access_reports',
                    requiresAIAssistant: true,
                    titleKey: 'app.navigation.aiAssistantUsageReport',
                },
            },
        ],
    },
    // ============== Misc Routes ==============
    {
        path: '/error',
        name: 'error',
        // common is always loaded eventually via initializeLanguage() at app bootstrap, but that
        // call isn't awaited before the router mounts — on a cold load straight to this route,
        // declaring i18nModule here makes the beforeEach guard's loadTranslations() await it
        // before navigation resolves, so the titleKey below doesn't race it.
        meta: { noAuth: true, i18nModule: 'common', titleKey: 'errors.noPageHere' },
        component: NotFoundView,
    },
    {
        path: '/:pathMatch(.*)*',
        meta: { noAuth: true },
        redirect: '/error',
    },
    {
        path: '/not-authorized',
        name: 'notAuthorized',
        meta: { noAuth: true, i18nModule: 'auth', titleKey: 'errors.frontend.notAuthorized' },
        component: NotAuthorizedView,
    },
];

export default routes;

const logger = Logger.get('RouteLazyLoading');

/*
REMARK: This function is taken from https://github.com/boydaihungst/vue-enterprise-boilerplate/blob/main/src/router/routes.ts

---------------------- Original comments: ----------------------------

Lazy-loads view components, but with better UX. A loading view
will be used if the component takes a while to load, falling
back to a timeout view in case the page fails to load. You can
use this component to lazy-load a route with:

component: () => lazyLoadView(import('@views/my-view'))

NOTE: Components loaded with this strategy DO NOT have access
to in-component guards, such as beforeRouteEnter,
beforeRouteUpdate, and beforeRouteLeave. You must either use
route-level guards instead or lazy-load the component directly:

component: () => import('@views/my-view')
*/
export function lazyLoadView(AsyncView: Promise<Component>): Promise<Component> {
    const AsyncHandler = defineAsyncComponent({
        loader: () => AsyncView,
        // A component to use while the component is loading.
        loadingComponent: LoadingView,
        // Delay before showing the loading component.
        // Default: 200 (milliseconds).
        delay: 400,
        // A fallback component in case the timeout is exceeded
        // when loading the component.
        errorComponent: TimeoutView,
        // Time before giving up trying to load the component.
        // Default: Infinity (milliseconds).
        timeout: 10000,
        /**
         *
         * @param {*} error Error message object
         * @param {*} retry A function that indicating whether the async component should retry when the loader promise rejects
         * @param {*} fail  End of failure
         * @param {*} attempts Maximum allowed retries number
         */
        onError(error, retry, fail, attempts) {
            logger.error(error, attempts);
            if (error.message.match(/fetch/) && attempts <= 3) {
                // retry on fetch errors, 3 max attempts
                retry();
            } else {
                // Note that retry/fail are like resolve/reject of a promise:
                // one of them must be called for the error handling to continue.
                fail();
            }
        },
    });

    return Promise.resolve(
        defineComponent({
            setup(props, { attrs, slots }) {
                return () => h(AsyncHandler, { ...attrs, ...props }, slots);
            },
        }),
    );
}
